import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiX,
  FiSearch,
  FiHeart,
  FiCalendar,
  FiMessageCircle,
  FiEye,
  FiLogIn,
  FiUser,
  FiHome,
  FiClock,
  FiFilter,
} from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './UserActivityHistoryModal.css';

const TYPE_META = {
  search: { label: 'Search', icon: FiSearch, color: '#2563eb', bg: '#eff6ff' },
  wishlist: { label: 'Wishlist', icon: FiHeart, color: '#e11d48', bg: '#fff1f2' },
  booking: { label: 'Booking', icon: FiCalendar, color: '#c8a04a', bg: '#fffbeb' },
  enquiry: { label: 'Enquiry', icon: FiMessageCircle, color: '#0d9488', bg: '#ecfdf5' },
  property_view: { label: 'Property View', icon: FiEye, color: '#7c3aed', bg: '#f5f3ff' },
  login: { label: 'Login', icon: FiLogIn, color: '#475569', bg: '#f1f5f9' },
  account: { label: 'Account', icon: FiUser, color: '#334155', bg: '#f8fafc' },
  listing: { label: 'Listing', icon: FiHome, color: '#16a34a', bg: '#ecfdf5' },
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const UserActivityHistoryModal = ({ user, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeCounts, setTypeCounts] = useState({});

  const authHeaders = { Authorization: `Bearer ${getAuthToken()}` };

  const fetchHistory = useCallback(async (pageNum, append = false) => {
    if (!user?.id) return;
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const res = await axios.get(`${API_URL}/admin/users/${user.id}/activity-history`, {
        headers: authHeaders,
        params: {
          page: pageNum,
          limit: 20,
          type: typeFilter === 'all' ? '' : typeFilter,
          q: search,
        },
      });

      const nextItems = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setHasMore(Boolean(res.data?.pagination?.hasMore));
      setTotal(res.data?.pagination?.total || 0);
      setTypeCounts(res.data?.type_counts || {});
      setPage(pageNum);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load activity history');
      if (!append) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user?.id, typeFilter, search, authHeaders.Authorization]);

  useEffect(() => {
    if (user?.id) fetchHistory(1, false);
  }, [user?.id, typeFilter, search, fetchHistory]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchHistory(page + 1, true);
  };

  if (!user) return null;

  return (
    <div className="admin-modal-overlay animate-fade-in uah-overlay" onClick={onClose}>
      <div
        className="admin-modal uah-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="uah-title"
      >
        <div className="modal-header uah-header">
          <div>
            <h2 id="uah-title">Activity History</h2>
            <p className="uah-subtitle">
              {user.user_name} · {user.email}
              {total > 0 && <span className="uah-total"> · {total} activities</span>}
            </p>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="uah-toolbar">
          <form className="uah-search" onSubmit={handleSearchSubmit}>
            <FiSearch className="uah-search-icon" />
            <input
              type="search"
              placeholder="Search activities…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn-secondary uah-search-btn">Search</button>
          </form>

          <div className="uah-filter">
            <FiFilter aria-hidden />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All types ({typeCounts.all || 0})</option>
              {Object.entries(TYPE_META).map(([key, meta]) => (
                <option key={key} value={key}>
                  {meta.label} ({typeCounts[key] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="uah-body">
          {loading ? (
            <div className="uah-loading">
              <div className="skeleton" style={{ height: 72, borderRadius: 10, marginBottom: 10 }} />
              <div className="skeleton" style={{ height: 72, borderRadius: 10, marginBottom: 10 }} />
              <div className="skeleton" style={{ height: 72, borderRadius: 10 }} />
            </div>
          ) : items.length === 0 ? (
            <div className="uah-empty">
              <FiClock size={36} />
              <p>No activity found for this user.</p>
            </div>
          ) : (
            <ul className="uah-list">
              {items.map((item) => {
                const meta = TYPE_META[item.type] || TYPE_META.search;
                const Icon = meta.icon;
                return (
                  <li key={item.id} className="uah-item">
                    <span
                      className="uah-item__icon"
                      style={{ background: meta.bg, color: meta.color }}
                      aria-hidden
                    >
                      <Icon size={18} />
                    </span>
                    <div className="uah-item__content">
                      <div className="uah-item__top">
                        <span className="uah-item__type" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                        <time className="uah-item__time" dateTime={item.occurred_at}>
                          {formatWhen(item.occurred_at)}
                        </time>
                      </div>
                      <p className="uah-item__label">{item.label}</p>
                      {item.detail && (
                        <p className="uah-item__detail">{item.detail}</p>
                      )}
                      {item.url && item.property_id && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="uah-item__link"
                        >
                          View property #{item.property_id}
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {hasMore && !loading && (
            <div className="uah-more">
              <button
                type="button"
                className="btn-secondary"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more activities'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserActivityHistoryModal;
